// Who can open a document or source in SharePoint (core visibility.ts): the details in a
// document's side panel, and a source's in its panel on Sources.

import { VISIBILITY_LABELS, isHidden, setOnText, type SourceVisibility, type Visibility } from "@ask-hr/core/visibility";

const checked = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

// How many of Ask HR - Users can open it, for the badge's tooltip and the details.
export const reachText = (v: Visibility) => `${v.reach} of the ${v.of} people in Ask HR - Users can open it.`;

// Under a document: who can open it, why, and what to change in SharePoint.
export function VisibilityDetail({
  v,
  webUrl,
  intended,
  busy,
  onIntended,
}: {
  v: Visibility;
  webUrl: string;
  intended: { by: string; at: string } | undefined;
  busy: boolean;
  onIntended: (intended: boolean) => void;
}) {
  const hidden = isHidden(v);
  return (
    <div className="review">
      {v.state === "everyone" ? (
        <p className="vis-why ok">
          <strong>Employees can open this file.</strong> {reachText(v)}
        </p>
      ) : v.state === "unknown" ? (
        <p className="vis-why">
          <strong>Ask HR can't tell who can open this file.</strong> It couldn't list the members of {v.unresolved?.join(", ")}.
        </p>
      ) : (
        <p className="vis-why">
          <strong>Most employees can't open this file from an answer.</strong> {reachText(v)} Everyone else gets SharePoint's "You need
          access" page. Ask HR still answers from it; only the link in the answer fails.
        </p>
      )}
      <dl className="vis-grid">
        <dt>Has access</dt>
        <dd>{v.who.length ? v.who.join(" · ") : "Nobody listed"}</dd>
        {v.link ? (
          <>
            <dt>Organization link</dt>
            <dd>
              Yes ({v.link === "edit" ? "edit" : "view"}). It only works for people who open that link, not the address answers cite, so it
              isn't counted.
            </dd>
          </>
        ) : null}
        <dt>Where it's set</dt>
        <dd>{setOnText(v.setOn)}</dd>
        {hidden ? (
          <>
            <dt>To fix it</dt>
            <dd>
              In SharePoint, give {v.setOn && v.setOn !== "/" ? `the folder ${v.setOn}` : "the file"} Read access for everyone (for example
              Novara All Company Members), or have it inherit the site's permissions again. Sync now picks up the change (the sync checks on its own every six hours).
            </dd>
          </>
        ) : null}
      </dl>
      <div className="actions start">
        <a href={webUrl} target="_blank" rel="noreferrer">
          Open in SharePoint
        </a>
        {hidden ? (
          <button type="button" disabled={busy} onClick={() => onIntended(!intended)}>
            {intended ? "Not intended" : "Mark as intended"}
          </button>
        ) : null}
        <span className="small muted">
          {intended ? `Marked as intended by ${intended.by}, ${checked(intended.at)}. ` : hidden ? "Mark as intended if HR keeps it private on purpose; it then stays out of Needs attention. " : ""}
          Checked {checked(v.checkedAt)}.
        </span>
      </div>
    </div>
  );
}

export function SourceAccessDetail({ v }: { v: SourceVisibility }) {
  const total = Object.values(v.docs).reduce((a, b) => a + b, 0);
  const hidden = v.docs.some + v.docs.link;
  const counts = (["everyone", "link", "some", "unknown"] as const)
    .filter((s) => v.docs[s])
    .map((s) => `${VISIBILITY_LABELS[s]} ${v.docs[s]}`)
    .join(" · ");
  return (
    <div className="review">
      <dl className="vis-grid">
        <dt>Documents</dt>
        <dd>
          {total
            ? hidden
              ? `Most employees can't open ${hidden} of ${total}. On Documents, open Filters and choose Permissions: Employees can't open to see them.`
              : `Employees can open all ${total}.`
            : "None yet."}
          {counts ? <div className="muted small">{counts}</div> : null}
        </dd>
        <dt>The folder</dt>
        <dd>
          {VISIBILITY_LABELS[v.state]}: {reachText(v)}
        </dd>
        <dt>Has access</dt>
        <dd>{v.who.length ? v.who.join(" · ") : "Nobody listed"}</dd>
        <dt>Where it's set</dt>
        <dd>{setOnText(v.setOn)}</dd>
      </dl>
      <p className="small muted">Checked {checked(v.checkedAt)}.</p>
    </div>
  );
}
